import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Loader2, CheckCircle2, ShieldAlert } from 'lucide-react';
import CommonHeader from '../components/CommonHeader';
import { verifyResetToken, updatePassword } from '../api/studentApi';
import PasswordValidator from '../components/PasswordValidator';
import { toast } from 'react-toastify';
import './PasswordUpdatePage.css';

const PasswordUpdatePage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [username, setUsername] = useState(''); // Optional: if you want to display the username/email associated with the reset link for extra confirmation to the user

    // States: null = verifying, true = valid link, false = link is dead/expired
    const [isTokenValid, setIsTokenValid] = useState(null);

    // Form Field States
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isPasswordValid, setIsPasswordValid] = useState(false);

    // Status State
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);

    const passwordsMatch = password && password === confirmPassword;

    // Verify token on mount
    useEffect(() => {
        const checkToken = async () => {
            if (!token || token.length !== 64) {
                setIsTokenValid(false);
                return;
            }
            try {
                const response = await verifyResetToken(token);
                if (response.status === 200) {
                    setIsTokenValid(true);
                    setUsername(response.data.username); // Set the username if available
                } else {
                    setIsTokenValid(false);
                }
            } catch (err) {
                setIsTokenValid(false);
                // Optional: display a specific message from your backend response
                setError(err.response?.data?.message || '');
            }
        };

        checkToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!isPasswordValid) {
            setError('Password does not meet complexity rules.');
            return;
        }

        if (!passwordsMatch) {
            setError('Passwords do not match.');
            return;
        }

        setIsLoading(true);

        try {
            await updatePassword(token, password);
            setIsSuccess(true);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update password. Link may be expired.');
            toast.error(err.response?.data?.message || "Failed to update password.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="password-update-page">
            <div className="password-update-nav">
                <CommonHeader />
            </div>
            <div className="password-update-container">
                <div className="password-update-card">

                    {/* STEP 1: Still verifying token validity on backend */}
                    {isTokenValid === null && (
                        <div className="update-loading-state">
                            <Loader2 size={40} className="animate-spin status-spinner" />
                            <p>Verifying secure link safety details...</p>
                        </div>
                    )}

                    {/* STEP 2: Token is invalid, expired, or tampered with */}
                    {isTokenValid === false && (
                        <div className="update-error-state">
                            <ShieldAlert size={52} className="error-icon-alert" />
                            <h2>Invalid or Expired Link</h2>
                            <p>
                                {error || "This password reset link is invalid, has already been used, or has expired for your security."}
                            </p>
                            <button className="btn-update-submit" onClick={() => window.location.href = "/login"}>
                                Back to Login
                            </button>
                        </div>
                    )}

                    {/* STEP 3: Token is valid and form has not been submitted yet */}
                    {isTokenValid === true && !isSuccess && (
                        <>
                            <div className="update-header">
                                <h2>Create New Password</h2>
                                <h4>Hello {username}</h4>
                            </div>

                            {error && <div className="update-error">{error}</div>}

                            <form onSubmit={handleSubmit} className="update-form">
                                <PasswordValidator
                                    value={password}
                                    onChange={setPassword}
                                    onValidityChange={setIsPasswordValid}
                                    disabled={isLoading}
                                />

                                <div className="input-group">
                                    <label htmlFor="confirm-password">Confirm New Password</label>
                                    <div className="input-field-wrapper">
                                        <Lock className="input-icon" size={20} />
                                        <input
                                            id="confirm-password"
                                            type="password"
                                            placeholder="Confirm new password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            disabled={isLoading}
                                            className={confirmPassword ? (passwordsMatch ? 'match-valid' : 'match-invalid') : ''}
                                        />
                                    </div>
                                    {confirmPassword && !passwordsMatch && (
                                        <span className="match-error-text">Passwords do not match yet.</span>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="btn-update-submit"
                                    disabled={isLoading || !isPasswordValid || !passwordsMatch}
                                >
                                    {isLoading ? (
                                        <>Updating Password... <Loader2 size={20} className="animate-spin" /></>
                                    ) : (
                                        'Reset Password'
                                    )}
                                </button>
                            </form>
                        </>
                    )}

                    {/* STEP 4: Password updated successfully */}
                    {isTokenValid === true && isSuccess && (
                        <div className="update-success-state">
                            <CheckCircle2 size={50} color="#16a34a" className="success-icon" />
                            <h2>Password Updated!</h2>
                            <p>Your password has been changed successfully. You can now log in with your new credentials.</p>
                            <button className="btn-update-submit" onClick={() => navigate('/login')}>
                                Go to Login
                            </button>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default PasswordUpdatePage;